import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  Chip,
  Switch,
  FormControlLabel,
  IconButton,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  DialogContentText,
  Menu,
  MenuItem,
  Tooltip,
} from '@mui/material';
import {
  DataGrid,
  GridActionsCellItem,
  GridToolbarContainer,
  GridToolbarFilterButton,
  GridToolbarColumnsButton,
  useGridApiRef,
  GridToolbarExport,
} from '@mui/x-data-grid';
import {
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  AccountTree as ModuleIcon,
  Settings as SettingsIcon,
  ExpandMore as ExpandMoreIcon,
  PlayArrow as ActionIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { modulesAPI, moduleActionsAPI } from '../../services/api';
import { usePermissions } from '../../contexts/PermissionContext';
import { AutoCloseAlert } from '../Common';
import { isActiveStatus } from '../../config/businessConstants';


const Modules = () => {
  const { hasPermission } = usePermissions();
  const [modules, setModules] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [actionsDialogOpen, setActionsDialogOpen] = useState(false);
  const [editingModule, setEditingModule] = useState(null);
  const [selectedModule, setSelectedModule] = useState(null);
  const [moduleActions, setModuleActions] = useState([]);
  const [viewMode, setViewMode] = useState(false);
  const [formData, setFormData] = useState({
    module_name: '',
    route_name: '',
    description: '',
    icon: '',
    is_active: true,
    is_core: false,
    order_index: 0,
  });
  const [formErrors, setFormErrors] = useState({
    module_name: '',
    route_name: '',
  });
  const [actionFormData, setActionFormData] = useState({
    action_name: '',
    action_url: '',
    status: true,
  });
  const [actionFormErrors, setActionFormErrors] = useState({
    action_name: '',
    action_url: ''
  });
  const [editingAction, setEditingAction] = useState(null);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [moduleToDelete, setModuleToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState('');
  const [deleteActionDialogOpen, setDeleteActionDialogOpen] = useState(false);
  const [actionToDelete, setActionToDelete] = useState(null);
  const [forceDeleteDialogOpen, setForceDeleteDialogOpen] = useState(false);
  const [forceDeleteInfo, setForceDeleteInfo] = useState(null);
  const apiRef = useGridApiRef();

  // Add state for filter model
  const [filterModel, setFilterModel] = useState({
    items: []
  });
  
  // Add state for column visibility
  const [columnVisibilityModel, setColumnVisibilityModel] = useState({});

  // Server-side processing state
  const [paginationModel, setPaginationModel] = useState({ page: 0, pageSize: 10 });
  const [sortModel, setSortModel] = useState([{ field: 'order_index', sort: 'asc' }]);
  const [rowCount, setRowCount] = useState(0);

  const canView = hasPermission('Modules', 'view');
  const canCreate = hasPermission('Modules', 'create');
  const canUpdate = hasPermission('Modules', 'update');
  const canDelete = hasPermission('Modules', 'delete');
  const canExport = hasPermission('Modules', 'export');

  const loadModules = async () => {
    if (!canView) return;
    setLoading(true);
    try {
      const params = {
        page: paginationModel.page + 1, // API is 1-based, MUI is 0-based
        per_page: paginationModel.pageSize,
        sort_field: sortModel.length > 0 ? sortModel[0].field : 'order_index',
        sort_order: sortModel.length > 0 ? sortModel[0].sort : 'asc',
        // Add server-side filtering
        filters: filterModel.items && filterModel.items.length > 0 ? 
          JSON.stringify(filterModel.items) : undefined,
      };
      const response = await modulesAPI.getModules(params);
      setModules(response.data.data);
      setRowCount(response.data.total);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to load modules.');
      console.error("Error loading modules:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadModules();
    // Remove company and superadmin loading calls
  }, [canView, paginationModel, sortModel, filterModel]);

  // Debug: Monitor column visibility changes
  useEffect(() => {
    console.log('Column visibility model changed:', columnVisibilityModel);
  }, [columnVisibilityModel]);

  const handleExportCsv = async () => {
    if (!hasPermission('Modules', 'export')) return;
    try {
      // Debug: Log the current state
      console.log('=== Export Debug Info ===');
      console.log('Column visibility model:', columnVisibilityModel);
      console.log('All columns:', columns.map(col => col.field));
      
      // Get visible columns based on columnVisibilityModel
      // In MUI X DataGrid, hidden columns have value 'false' in columnVisibilityModel
      const visibleColumns = columns
        .filter(col => {
          // Always exclude these columns
          if (col.field === '__check__' || col.field === 'actions') {
            return false;
          }
          // Include column if it's visible (not in columnVisibilityModel or value is not false)
          return columnVisibilityModel[col.field] !== false;
        })
        .map(col => col.field);
      
      console.log('Visible columns for export:', visibleColumns);
      console.log('Hidden columns:', Object.keys(columnVisibilityModel).filter(key => columnVisibilityModel[key] === false));
      
      const params = {
        export: 'csv',
        sort_field: sortModel.length > 0 ? sortModel[0].field : 'order_index',
        sort_order: sortModel.length > 0 ? sortModel[0].sort : 'asc',
        filters: filterModel.items && filterModel.items.length > 0 ? 
          JSON.stringify(filterModel.items) : undefined,
        visible_columns: visibleColumns.length > 0 ? JSON.stringify(visibleColumns) : undefined
      };
      
      console.log('Export params:', params);
      console.log('=== End Export Debug Info ===');
      
      // Note: The backend API needs to handle these filter and column parameters
      const response = await modulesAPI.getModules(params);

      // Create a blob from the CSV data and trigger a download
      const blob = new Blob([response.data], { type: 'text/csv' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', 'modules.csv');
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to export modules.');
      console.error('Error exporting modules:', err);
    }
  };

  const CustomToolbar = () => {
    return (
      <GridToolbarContainer>
        <GridToolbarColumnsButton />
        <GridToolbarFilterButton />
        {hasPermission('Modules', 'export') && (
          <GridToolbarExport 
            printOptions={{ disableToolbarButton: true }}
            csvOptions={{ 
              disableToolbarButton: false,
              fileName: 'Modules', // Custom filename for CSV export
              getRowsToExport: () => {
                // When CSV is clicked, call our custom export handler
                // This will include current filters and column visibility settings
                handleExportCsv();
                // Return empty array since we're handling export via API
                return [];
              }
            }}
          />
        )}
        {hasPermission('Modules', 'create') && (
          <Button startIcon={<AddIcon />} onClick={handleAdd}>
            Add Module
          </Button>
        )}
      </GridToolbarContainer>
    );
  };

  const loadModuleActions = async (moduleId) => {
    try {
      const response = await moduleActionsAPI.getModuleActions(moduleId);
      setModuleActions(response.data.data || []);
    } catch (error) {
      console.error('Error loading module actions:', error);
      setError(
        (error.response && error.response.data && error.response.data.error) || 
        (error.response && error.response.data && error.response.data.message) ||
        'Failed to load module actions'
      );
    }
  };

  const handleAdd = () => {
    setEditingModule(null);
    setViewMode(false);
    setFormData({
      module_name: '',
      route_name: '',
      description: '',
      icon: '',
      is_active: true,
      is_core: false,
      order_index: 0,
    });
    setFormErrors({
      module_name: '',
      route_name: '',
    });
    setDialogOpen(true);
  };

  const handleEdit = (module) => {
    setEditingModule(module);
    setViewMode(false);
    setFormData({
      module_name: module.module_name,
      route_name: module.route_name || '',
      description: module.description || '',
      icon: module.icon || '',
      is_active: module.is_active,
      is_core: module.is_core || false,
      order_index: module.order_index || 0,
    });
    setFormErrors({
      module_name: '',
      route_name: '',
    });
    setDialogOpen(true);
  };

  const handleView = (module) => {
    setEditingModule(module);
    setViewMode(true);
    setFormData({
      module_name: module.module_name,
      route_name: module.route_name || '',
      description: module.description || '',
      icon: module.icon || '',
      is_active: module.is_active,
      is_core: module.is_core || false,
      order_index: module.order_index || 0,
    });
    setFormErrors({
      module_name: '',
      route_name: '',
    });
    setDialogOpen(true);
  };

  const handleManageActions = (module) => {
    setSelectedModule(module);
    loadModuleActions(module.id);
    setActionsDialogOpen(true);
  };

  const handleDelete = (module) => {
    setModuleToDelete(module);
    setDeleteError(''); // Clear previous errors
    setDeleteDialogOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (moduleToDelete) {
      try {
        await modulesAPI.delete(moduleToDelete.id);
        setSuccess('Module deleted successfully');
        setDeleteDialogOpen(false);
        setModuleToDelete(null);
        
        // Check if we need to go back to previous page if current page becomes empty
        const currentPageStartIndex = paginationModel.page * paginationModel.pageSize;
        const totalItemsAfterDelete = rowCount - 1;
        
        if (currentPageStartIndex >= totalItemsAfterDelete && paginationModel.page > 0) {
          setPaginationModel(prev => ({ ...prev, page: prev.page - 1 }));
        } else {
        loadModules();
        }
      } catch (error) {
        setDeleteError(
          (error.response && error.response.data && error.response.data.error) || 
          (error.response && error.response.data && error.response.data.message) ||
          'Failed to delete module'
        );
      }
    }
  };

  const handleCancelDelete = () => {
    setDeleteDialogOpen(false);
    setModuleToDelete(null);
    setDeleteError(''); // Clear error on cancel
  };

  const validateForm = () => {
    const errors = {
      module_name: '',
      route_name: '',
    };
    let isValid = true;

    // Validate module name
    if (!formData.module_name.trim()) {
      errors.module_name = 'Module name is required';
      isValid = false;
    } else if (formData.module_name.length > 50) {
      errors.module_name = 'Module name must be 50 characters or less';
      isValid = false;
    }

    // Validate route name (if provided)
    if (formData.route_name.trim()) {
      // Check for consecutive slashes
      if (formData.route_name.includes('//')) {
        errors.route_name = 'Route name cannot contain consecutive slashes';
        isValid = false;
      } 
      // Check for leading/trailing slashes
      else if (formData.route_name.startsWith('/') || formData.route_name.endsWith('/')) {
        errors.route_name = 'Route name cannot start or end with a slash';
        isValid = false;
      }
      // Check for special characters (except single slash and alphanumeric)
      else if (!/^[a-zA-Z0-9/_-]+$/.test(formData.route_name)) {
        errors.route_name = 'Route name can only contain letters, numbers, hyphens, underscores, and single slashes';
        isValid = false;
      }
      // Check length
      else if (formData.route_name.length > 50) {
        errors.route_name = 'Route name must be 50 characters or less';
        isValid = false;
      }
    }

    setFormErrors(errors);
    return isValid;
  };

  // Validate action form
  const validateActionForm = () => {
    const errors = {
      action_name: '',
      action_url: ''
    };
    let isValid = true;

    // Validate action name
    if (!actionFormData.action_name.trim()) {
      errors.action_name = 'Action name is required';
      isValid = false;
    } else if (actionFormData.action_name.length > 30) {
      errors.action_name = 'Action name must be 30 characters or less';
      isValid = false;
    } else if (!/^[a-zA-Z0-9_-]+$/.test(actionFormData.action_name)) {
      errors.action_name = 'Action name can only contain letters, numbers, hyphens, and underscores';
      isValid = false;
    }

    // Validate action URL
    if (!actionFormData.action_url.trim()) {
      errors.action_url = 'Action URL is required';
      isValid = false;
    } else if (actionFormData.action_url.length > 50) {
      errors.action_url = 'Action URL must be 50 characters or less';
      isValid = false;
    }
    // Check for consecutive slashes
    else if (actionFormData.action_url.includes('//')) {
      errors.action_url = 'Action URL cannot contain consecutive slashes';
      isValid = false;
    }
    // Check for special characters
    else if (!/^[a-zA-Z0-9/_-]+$/.test(actionFormData.action_url)) {
      errors.action_url = 'Action URL can only contain letters, numbers, hyphens, underscores, and slashes';
      isValid = false;
    }

    setActionFormErrors(errors);
    return isValid;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    // Run validation
    if (!validateForm()) {
      return;
    }

    try {
      const submitData = {
        ...formData,
        is_active: formData.is_active,
        is_core: formData.is_core,
        status: formData.is_active ? 1 : 0,
      };

      let successMessage = '';
      let responseData = null;

      if (editingModule) {
        const response = await modulesAPI.update(editingModule.id, submitData);
        responseData = response.data;
        successMessage = 'Module updated successfully';
      } else {
        const response = await modulesAPI.create(submitData);
        responseData = response.data;
        successMessage = 'Module created successfully';
        
        // If module was created successfully, optionally create default actions
        if (responseData && responseData.module_id) {
          try {
            await moduleActionsAPI.createBulkActions(responseData.module_id, [
              { action_name: 'view', action_url: '/view' },
              { action_name: 'create', action_url: '/create' },
              { action_name: 'update', action_url: '/update' },
              { action_name: 'delete', action_url: '/delete' },
            ]);
          } catch (actionError) {
            console.warn('Failed to create default actions:', actionError);
          }
        }
      }
      
      // Check if order index was adjusted
      if (responseData && responseData.note) {
        successMessage += `. ${responseData.note}`;
      }
      
      setSuccess(successMessage);
      setDialogOpen(false);
      
      // For new modules, calculate the page where the new item should appear
      // Since modules are ordered by order_index and module_name, go to first page
      if (!editingModule) {
        setPaginationModel(prev => ({ ...prev, page: 0 }));
      }
      
      loadModules(); // Reload current page
    } catch (error) {
      setError((error.response && error.response.data && error.response.data.error) || 'Operation failed');
    }
  };

  const handleActionSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validate form
    if (!validateActionForm()) {
      return;
    }

    try {
      if (editingAction) {
        await moduleActionsAPI.updateAction(editingAction.id, {
          action_name: actionFormData.action_name,
          action_url: actionFormData.action_url,
          status: actionFormData.status ? 1 : 0,
        });
        setSuccess('Action updated successfully');
      } else {
        await moduleActionsAPI.createAction(selectedModule.id, {
          action_name: actionFormData.action_name,
          action_url: actionFormData.action_url,
          status: actionFormData.status ? 1 : 0,
        });
        setSuccess('Action created successfully');
      }
      
      setActionFormData({ action_name: '', action_url: '', status: true });
      setActionFormErrors({ action_name: '', action_url: '' });
      setEditingAction(null);
      loadModuleActions(selectedModule.id);
    } catch (error) {
      setError(
        (error.response && error.response.data && error.response.data.error) || 
        (error.response && error.response.data && error.response.data.message) ||
        'Failed to save action'
      );
    }
  };

  const handleEditAction = (action) => {
    setEditingAction(action);
    setActionFormData({
      action_name: action.action_name,
      action_url: action.action_url,
      status: isActiveStatus(action.status),
    });
  };

  const handleDeleteAction = (action) => {
    setActionToDelete(action);
    setDeleteActionDialogOpen(true);
  };

  const handleConfirmDeleteAction = async (forceDelete = false) => {
    if (actionToDelete) {
      try {
        await moduleActionsAPI.deleteAction(actionToDelete.id, forceDelete);
        setSuccess('Action deleted successfully');
        setDeleteActionDialogOpen(false);
        setForceDeleteDialogOpen(false);
        setActionToDelete(null);
        setForceDeleteInfo(null);
        loadModuleActions(selectedModule.id);
      } catch (error) {
        const errorData = error.response?.data;
        
        // If the error indicates that the action is used in permissions and can be force deleted
        if (errorData?.can_force_delete && !forceDelete) {
          setForceDeleteInfo({
            action: actionToDelete,
            error: errorData.error,
            details: errorData.details
          });
          setDeleteActionDialogOpen(false);
          setForceDeleteDialogOpen(true);
        } else {
          setError(
            errorData?.error || 
            errorData?.message ||
            'Failed to delete action'
          );
          setDeleteActionDialogOpen(false);
          setForceDeleteDialogOpen(false);
          setActionToDelete(null);
          setForceDeleteInfo(null);
        }
      }
    }
  };

  const handleCancelDeleteAction = () => {
    setDeleteActionDialogOpen(false);
    setActionToDelete(null);
  };

  const handleCancelForceDeleteAction = () => {
    setForceDeleteDialogOpen(false);
    setForceDeleteInfo(null);
    setActionToDelete(null);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setError('');
    setEditingModule(null);
    setViewMode(false);
    setFormErrors({
      module_name: '',
      route_name: '',
    });
  };

  const handleCloseActionsDialog = () => {
    setActionsDialogOpen(false);
    setSelectedModule(null);
    setModuleActions([]);
    setActionFormData({ action_name: '', action_url: '', status: true });
    setEditingAction(null);
    setDeleteActionDialogOpen(false);
    setForceDeleteDialogOpen(false);
    setActionToDelete(null);
    setForceDeleteInfo(null);
    setError('');
  };

  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    {
      field: 'module_name',
      headerName: 'Module Name',
      width: 200,
      renderCell: (params) => (
        <Box display="flex" alignItems="center">
          <ModuleIcon sx={{ mr: 1, color: 'primary.main' }} />
          {params.value}
        </Box>
      ),
    },
    {
      field: 'display_route',
      headerName: 'Route',
      width: 150,
      renderCell: (params) => (
        <Chip
          label={`/${params.value}`}
          size="small"
          color="primary"
          variant="outlined"
        />
      ),
    },
    { 
      field: 'order_index', 
      headerName: 'Order', 
      width: 80,
      renderCell: (params) => (
        <Chip
          label={params.value || 0}
          size="small"
          color="secondary"
          variant="outlined"
        />
      ),
    },
    { field: 'description', headerName: 'Description', width: 250 },
    { field: 'icon', headerName: 'Icon', width: 100 },
    {
      field: 'is_active',
      headerName: 'Status',
      width: 120,
      type: 'string',
      valueGetter: (params) => {
        // Return "Active" or "Inactive" text for filtering instead of boolean
        return params.value ? 'Active' : 'Inactive';
      },
      renderCell: (params) => {
        // Use the raw row data to get the actual boolean value
        const isActive = params.row.is_active;
        return (
          <Chip
            label={isActive ? 'Active' : 'Inactive'}
            color={isActive ? 'success' : 'error'}
            size="small"
          />
        );
      },
    },

    {
      field: 'is_core',
      headerName: 'Core Module',
      width: 120,
      type: 'string',
      valueGetter: (params) => params.value ? 'Core' : 'Feature',
      renderCell: (params) => (
        <Chip
          label={params.row.is_core ? 'Core' : 'Feature'}
          color={params.row.is_core ? 'default' : 'primary'}
          size="small"
          variant={params.row.is_core ? 'outlined' : 'filled'}
        />
      ),
    },

    {
      field: 'created_at',
      headerName: 'Created',
      width: 150,
      type: 'string',
      valueGetter: (params) => {
        if (!params.value) return 'N/A';
        const date = new Date(params.value);
        if (isNaN(date.getTime())) return 'N/A';
        
        // Format date with leading zeros to show DD/MM/YYYY
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = date.getFullYear();
        
        return `${day}/${month}/${year}`;
      },
      sortComparator: (v1, v2) => {
        if (v1 === 'N/A' && v2 === 'N/A') return 0;
        if (v1 === 'N/A') return -1;
        if (v2 === 'N/A') return 1;
        
        // Convert DD/MM/YYYY back to Date objects for comparison
        const [day1, month1, year1] = v1.split('/').map(Number);
        const [day2, month2, year2] = v2.split('/').map(Number);
        
        const date1 = new Date(year1, month1 - 1, day1);
        const date2 = new Date(year2, month2 - 1, day2);
        
        return date1.getTime() - date2.getTime();
      },
    },
    {
      field: 'actions',
      type: 'actions',
      headerName: 'Actions',
      width: 200,
      getActions: (params) => {
        const actions = [];
        
        if (canView) {
          actions.push(
            <Tooltip title="View Module Details" arrow>
              <GridActionsCellItem
                icon={<ViewIcon />}
                label="View"
                onClick={() => handleView(params.row)}
              />
            </Tooltip>
          );
        }
        
        if (canUpdate) {
          actions.push(
            <Tooltip title="Edit Module" arrow>
              <GridActionsCellItem
                icon={<EditIcon />}
                label="Edit"
                onClick={() => handleEdit(params.row)}
              />
            </Tooltip>
          );
          
          actions.push(
            <Tooltip title="Manage Module Actions" arrow>
              <GridActionsCellItem
                icon={<SettingsIcon />}
                label="Manage Actions"
                onClick={() => handleManageActions(params.row)}
              />
            </Tooltip>
          );
        }
        
        if (canDelete) {
          actions.push(
            <Tooltip title="Delete Module" arrow>
              <GridActionsCellItem
                icon={<DeleteIcon />}
                label="Delete"
                onClick={() => handleDelete(params.row)}
              />
            </Tooltip>
          );
        }
        
        return actions;
      },
    },
  ];

  if (!canView) {
    return (
      <Box p={3}>
        <Alert severity="error">
          You don't have permission to view modules.
        </Alert>
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        Modules Management
      </Typography>
      
      <AutoCloseAlert
        severity="error"
        message={error}
        show={!!error}
        onClose={() => setError('')}
        autoCloseDelay={6000}
      />
      
      <AutoCloseAlert
        severity="success"
        message={success}
        show={!!success}
        onClose={() => setSuccess('')}
        autoCloseDelay={4000}
      />

      <Paper sx={{ height: 'auto', width: '100%' }}>
        <DataGrid
          rows={modules}
          columns={columns}
          loading={loading}
          paginationMode="server"
          sortingMode="server"
          filterMode="server"
          pagination
          pageSizeOptions={[5, 10, 25, 50, 100]}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          sortModel={sortModel}
          onSortModelChange={setSortModel}
          rowCount={rowCount}
          disableSelectionOnClick
          apiRef={apiRef}
          filterModel={filterModel}
          onFilterModelChange={setFilterModel}
          columnVisibilityModel={columnVisibilityModel}
          onColumnVisibilityModelChange={setColumnVisibilityModel}
          components={{
            Toolbar: CustomToolbar
          }}
        />
      </Paper>

      {/* Module Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
        <DialogTitle>
          {viewMode ? 'View Module' : editingModule ? 'Edit Module' : 'Add New Module'}
        </DialogTitle>
        <form onSubmit={handleSubmit} noValidate>
          <DialogContent>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            
            <TextField
              autoFocus
              margin="dense"
              label="Module Name"
              fullWidth
              variant="outlined"
              value={formData.module_name || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, module_name: value });
                // Clear error when user starts typing
                if (formErrors.module_name) {
                  setFormErrors({ ...formErrors, module_name: '' });
                }
              }}
              onBlur={() => {
                // Validate on blur
                if (!formData.module_name.trim()) {
                  setFormErrors({ ...formErrors, module_name: 'Module name is required' });
                } else if (formData.module_name.length > 50) {
                  setFormErrors({ ...formErrors, module_name: 'Module name must be 50 characters or less' });
                }
              }}
              disabled={viewMode}
              sx={{ mb: 2 }}
              helperText={formErrors.module_name || "Enter a unique module name (max 50 characters)"}
              error={!!formErrors.module_name}
              inputProps={{ maxLength: 50 }}
              FormHelperTextProps={{
                sx: {
                  color: formErrors.module_name ? 'error.main' : 'text.secondary',
                  fontWeight: formErrors.module_name ? 500 : 'normal'
                }
              }}
            />

            {/* Remove company dropdown - backend will auto-assign company */}

            <TextField
              margin="dense"
              label="Route Name (Optional)"
              fullWidth
              variant="outlined"
              value={formData.route_name || ''}
              onChange={(e) => {
                const value = e.target.value;
                setFormData({ ...formData, route_name: value });
                // Clear error when user starts typing
                if (formErrors.route_name) {
                  setFormErrors({ ...formErrors, route_name: '' });
                }
              }}
              onBlur={() => {
                // Validate on blur
                if (formData.route_name.trim()) {
                  if (formData.route_name.includes('//')) {
                    setFormErrors({ ...formErrors, route_name: 'Route name cannot contain consecutive slashes' });
                  } else if (formData.route_name.startsWith('/') || formData.route_name.endsWith('/')) {
                    setFormErrors({ ...formErrors, route_name: 'Route name cannot start or end with a slash' });
                  } else if (!/^[a-zA-Z0-9/_-]+$/.test(formData.route_name)) {
                    setFormErrors({ ...formErrors, route_name: 'Route name can only contain letters, numbers, hyphens, underscores, and single slashes' });
                  } else if (formData.route_name.length > 50) {
                    setFormErrors({ ...formErrors, route_name: 'Route name must be 50 characters or less' });
                  }
                }
              }}
              disabled={viewMode}
              sx={{ mb: 2 }}
              helperText={formErrors.route_name || "Custom route name (e.g., 'users/profile'). Letters, numbers, hyphens, underscores, and single slashes only."}
              error={!!formErrors.route_name}
              inputProps={{ maxLength: 50 }}
              FormHelperTextProps={{
                sx: {
                  color: formErrors.route_name ? 'error.main' : 'text.secondary',
                  fontWeight: formErrors.route_name ? 500 : 'normal'
                }
              }}
            />

            <TextField
              margin="dense"
              label="Icon"
              fullWidth
              variant="outlined"
              value={formData.icon}
              onChange={(e) => setFormData({ ...formData, icon: e.target.value })}
              disabled={viewMode}
              sx={{ mb: 2 }}
              helperText="Icon name for UI display (e.g., users, settings, dashboard)"
            />

            <TextField
              margin="dense"
              label="Description"
              fullWidth
              variant="outlined"
              multiline
              rows={3}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              disabled={viewMode}
              sx={{ mb: 2 }}
              helperText="Brief description of what this module manages"
            />

            <TextField
              margin="dense"
              label="Sort Order"
              type="number"
              fullWidth
              variant="outlined"
              value={formData.order_index || 0}
              onChange={(e) => setFormData({...formData, order_index: parseInt(e.target.value) || 0})}
              disabled={viewMode}
              sx={{ mb: 2 }}
              helperText="Order for sidebar display (lower numbers appear first). Must be unique - will be auto-adjusted if already in use."
              inputProps={{ min: 0 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_active}
                  onChange={(e) => setFormData({ ...formData, is_active: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Active Status"
              sx={{ mt: 1 }}
            />

            <FormControlLabel
              control={
                <Switch
                  checked={formData.is_core}
                  onChange={(e) => setFormData({ ...formData, is_core: e.target.checked })}
                  disabled={viewMode}
                />
              }
              label="Core Module"
              sx={{ mt: 1, ml: 2 }}
            />
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>
              {viewMode ? 'Close' : 'Cancel'}
            </Button>
            {!viewMode && (
              <Button type="submit" variant="contained">
                {editingModule ? 'Update' : 'Create'}
              </Button>
            )}
          </DialogActions>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={handleCancelDelete}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          Confirm Delete
        </DialogTitle>
        <DialogContent>
          {deleteError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          )}
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Are you sure you want to delete the module{' '}
            <strong>{moduleToDelete?.module_name}</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
            This action will mark the module as inactive and it will not be available in the system.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelDelete}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleConfirmDelete}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Module Actions Management Dialog */}
      <Dialog 
        open={actionsDialogOpen} 
        onClose={handleCloseActionsDialog} 
        maxWidth="md" 
        fullWidth
      >
        <DialogTitle>
          <Box display="flex" alignItems="center">
            <SettingsIcon sx={{ mr: 1 }} />
            Manage Actions for "{selectedModule?.module_name}"
          </Box>
        </DialogTitle>
        <DialogContent>
          {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
          {success && <Alert severity="success" sx={{ mb: 2 }}>{success}</Alert>}
          
          <Grid container spacing={3}>
            {/* Add/Edit Action Form */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    {editingAction ? 'Edit Action' : 'Add New Action'}
                  </Typography>
                  
                  <form onSubmit={handleActionSubmit} noValidate>
                    <TextField
                      margin="dense"
                      label="Action Name"
                      fullWidth
                      variant="outlined"
                      value={actionFormData.action_name}
                      onChange={(e) => {
                        const value = e.target.value;
                        setActionFormData({ ...actionFormData, action_name: value });
                        // Clear error when user starts typing
                        if (actionFormErrors.action_name) {
                          setActionFormErrors({ ...actionFormErrors, action_name: '' });
                        }
                      }}
                      onBlur={() => {
                        // Validate on blur
                        if (!actionFormData.action_name.trim()) {
                          setActionFormErrors({ ...actionFormErrors, action_name: 'Action name is required' });
                        } else if (actionFormData.action_name.length > 30) {
                          setActionFormErrors({ ...actionFormErrors, action_name: 'Action name must be 30 characters or less' });
                        } else if (!/^[a-zA-Z0-9_-]+$/.test(actionFormData.action_name)) {
                          setActionFormErrors({ ...actionFormErrors, action_name: 'Action name can only contain letters, numbers, hyphens, and underscores' });
                        }
                      }}
                      sx={{ mb: 2 }}
                      helperText={actionFormErrors.action_name || "e.g., view, create, update, delete, export, import (max 30 characters)"}
                      error={!!actionFormErrors.action_name}
                      inputProps={{ maxLength: 30 }}
                      FormHelperTextProps={{
                        sx: {
                          color: actionFormErrors.action_name ? 'error.main' : 'text.secondary',
                          fontWeight: actionFormErrors.action_name ? 500 : 'normal'
                        }
                      }}
                    />
                    
                    <TextField
                      margin="dense"
                      label="Action URL"
                      fullWidth
                      variant="outlined"
                      value={actionFormData.action_url}
                      onChange={(e) => {
                        const value = e.target.value;
                        setActionFormData({ ...actionFormData, action_url: value });
                        // Clear error when user starts typing
                        if (actionFormErrors.action_url) {
                          setActionFormErrors({ ...actionFormErrors, action_url: '' });
                        }
                      }}
                      onBlur={() => {
                        // Validate on blur
                        if (!actionFormData.action_url.trim()) {
                          setActionFormErrors({ ...actionFormErrors, action_url: 'Action URL is required' });
                        } else if (actionFormData.action_url.length > 50) {
                          setActionFormErrors({ ...actionFormErrors, action_url: 'Action URL must be 50 characters or less' });
                        } else if (actionFormData.action_url.includes('//')) {
                          setActionFormErrors({ ...actionFormErrors, action_url: 'Action URL cannot contain consecutive slashes' });
                        } else if (!/^[a-zA-Z0-9/_-]+$/.test(actionFormData.action_url)) {
                          setActionFormErrors({ ...actionFormErrors, action_url: 'Action URL can only contain letters, numbers, hyphens, underscores, and slashes' });
                        }
                      }}
                      sx={{ mb: 2 }}
                      helperText={actionFormErrors.action_url || "e.g., /view, /create, /export (max 50 characters)"}
                      error={!!actionFormErrors.action_url}
                      inputProps={{ maxLength: 50 }}
                      FormHelperTextProps={{
                        sx: {
                          color: actionFormErrors.action_url ? 'error.main' : 'text.secondary',
                          fontWeight: actionFormErrors.action_url ? 500 : 'normal'
                        }
                      }}
                    />
                    
                    <FormControlLabel
                      control={
                        <Switch
                          checked={actionFormData.status}
                          onChange={(e) => setActionFormData({ 
                            ...actionFormData, 
                            status: e.target.checked 
                          })}
                        />
                      }
                      label="Active"
                      sx={{ mb: 2 }}
                    />
                    
                    <Box>
                      <Button 
                        type="submit" 
                        variant="contained" 
                        startIcon={<AddIcon />}
                        sx={{ mr: 1 }}
                      >
                        {editingAction ? 'Update' : 'Add'} Action
                      </Button>
                      
                      {editingAction && (
                        <Button 
                          onClick={() => {
                            setEditingAction(null);
                            setActionFormData({ action_name: '', action_url: '', status: true });
                          }}
                        >
                          Cancel
                        </Button>
                      )}
                    </Box>
                  </form>
                </CardContent>
              </Card>
            </Grid>
            
            {/* Existing Actions List */}
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Current Actions ({moduleActions.length})
                  </Typography>
                  
                  {moduleActions.length === 0 ? (
                    <Typography color="text.secondary">
                      No actions defined for this module
                    </Typography>
                  ) : (
                    <List dense>
                      {moduleActions.map((action, index) => (
                        <React.Fragment key={action.id}>
                          <ListItem>
                            <ListItemText
                              primary={
                                <Box display="flex" alignItems="center">
                                  <ActionIcon sx={{ mr: 1, fontSize: 16 }} />
                                  {action.action_name}
                                  <Chip
                                    label={isActiveStatus(action.status) ? 'Active' : 'Inactive'}
                                    size="small"
                                    color={isActiveStatus(action.status) ? 'success' : 'default'}
                                    sx={{ ml: 1 }}
                                  />
                                </Box>
                              }
                              secondary={`URL: ${action.action_url}`}
                            />
                            <ListItemSecondaryAction>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleEditAction(action)}
                                sx={{ mr: 1 }}
                              >
                                <EditIcon fontSize="small" />
                              </IconButton>
                              <IconButton
                                edge="end"
                                size="small"
                                onClick={() => handleDeleteAction(action)}
                                color="error"
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </ListItemSecondaryAction>
                          </ListItem>
                          {index < moduleActions.length - 1 && <Divider />}
                        </React.Fragment>
                      ))}
                    </List>
                  )}
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseActionsDialog}>
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete Action Confirmation Dialog */}
      <Dialog
        open={deleteActionDialogOpen}
        onClose={handleCancelDeleteAction}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          Confirm Delete Action
        </DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            Are you sure you want to delete the action{' '}
            <strong>"{actionToDelete?.action_name}"</strong>?
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
            This action will be permanently removed from the module.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelDeleteAction}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleConfirmDeleteAction(false)}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 100 }}
            autoFocus
          >
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      {/* Force Delete Action Confirmation Dialog */}
      <Dialog
        open={forceDeleteDialogOpen}
        onClose={handleCancelForceDeleteAction}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle sx={{ 
          color: 'error.main', 
          fontWeight: 'bold',
          display: 'flex',
          alignItems: 'center',
          gap: 1
        }}>
          <DeleteIcon />
          Force Delete Action
        </DialogTitle>
        <DialogContent>
          <Alert severity="warning" sx={{ mb: 2 }}>
            {forceDeleteInfo?.error}
          </Alert>
          <DialogContentText sx={{ fontSize: '1.1rem', color: 'text.primary' }}>
            {forceDeleteInfo?.details}
          </DialogContentText>
          <DialogContentText sx={{ mt: 2, color: 'text.secondary' }}>
            Force deleting this action will also remove all related permissions. This action cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions sx={{ p: 3, gap: 2 }}>
          <Button 
            onClick={handleCancelForceDeleteAction}
            variant="outlined"
            size="large"
            sx={{ minWidth: 100 }}
          >
            Cancel
          </Button>
          <Button 
            onClick={() => handleConfirmDeleteAction(true)}
            variant="contained"
            color="error"
            size="large"
            sx={{ minWidth: 140 }}
            autoFocus
          >
            Force Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Modules; 